import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Buyer requests — the "wanted" side of the marketplace.
 *
 * A buyer says what they are looking for; sellers who have it respond through
 * the platform. The buyer's own phone/email are never selected here: column
 * privileges on buyer_requests keep them away from the browser entirely, and
 * sellers reply via buyer_request_responses instead.
 */

export interface BuyerRequest {
  id: string;
  buyer_id: string;
  category: string;
  title: string;
  description: string | null;
  species: string | null;
  breed: string | null;
  quantity: number | null;
  county: string | null;
  budget_min: number | null;
  budget_max: number | null;
  status: string;
  details: Record<string, string> | null;
  created_at: string;
  approved_at?: string | null;
  buyer?: {
    id: string;
    full_name: string | null;
    business_name: string | null;
    county: string | null;
    cph_verified: boolean | null;
    membership_tier: string | null;
  } | null;
  response_count?: number;
}

export interface BuyerRequestResponse {
  id: string;
  request_id: string;
  seller_id: string;
  message: string | null;
  created_at: string;
  seller?: {
    id: string;
    full_name: string | null;
    business_name: string | null;
    county: string | null;
  } | null;
}

/** Live buyer requests are 'open'; a fresh one is held at 'pending' for review. */
export const OPEN_STATUS = 'open';
export const NEW_REQUEST_STATUS = 'pending';

const COLUMNS =
  'id, buyer_id, category, title, description, species, breed, quantity, county, ' +
  'budget_min, budget_max, status, details, created_at, approved_at';

export interface FetchBuyerRequestsOptions {
  status?: string;
  category?: string;
  county?: string;
  q?: string;
  buyerId?: string;
  limit?: number;
  offset?: number;
  sort?: 'newest' | 'oldest';
}

export async function fetchBuyerRequests(
  supabase: SupabaseClient,
  opts: FetchBuyerRequestsOptions = {}
): Promise<{ requests: BuyerRequest[]; total: number }> {
  const {
    status = OPEN_STATUS,
    category,
    county,
    q,
    buyerId,
    limit = 24,
    offset = 0,
    sort = 'newest',
  } = opts;

  let query = supabase
    .from('buyer_requests')
    .select(COLUMNS, { count: 'exact' })
    .eq('status', status);

  if (category) query = query.eq('category', category);
  if (county) query = query.eq('county', county);
  if (buyerId) query = query.eq('buyer_id', buyerId);
  if (q && q.trim()) {
    const term = `%${q.trim()}%`;
    query = query.or(`title.ilike.${term},description.ilike.${term},breed.ilike.${term}`);
  }

  query = query
    .order('created_at', { ascending: sort === 'oldest' })
    .range(offset, offset + limit - 1);

  const { data, error, count } = await query;
  if (error) throw error;

  return { requests: (data ?? []) as unknown as BuyerRequest[], total: count ?? 0 };
}

/** A buyer's own requests, whatever their review status. */
export async function fetchMyBuyerRequests(
  supabase: SupabaseClient,
  buyerId: string
): Promise<BuyerRequest[]> {
  // The response count comes back with the row. Without it the dashboard shows
  // "0 responses" on a request sellers have actually replied to, which is the
  // one number the buyer came to look at.
  const { data, error } = await supabase
    .from('buyer_requests')
    .select(`${COLUMNS}, buyer_request_responses ( count )`)
    .eq('buyer_id', buyerId)
    .order('created_at', { ascending: false });
  if (error) throw error;

  type Row = BuyerRequest & { buyer_request_responses?: { count: number }[] };
  return ((data ?? []) as unknown as Row[]).map(({ buyer_request_responses, ...row }) => ({
    ...row,
    response_count: buyer_request_responses?.[0]?.count ?? 0,
  }));
}

export async function fetchBuyerRequest(
  supabase: SupabaseClient,
  id: string
): Promise<BuyerRequest | null> {
  const { data, error } = await supabase
    .from('buyer_requests')
    .select(
      `${COLUMNS}, buyer:profiles!buyer_requests_buyer_id_fkey ( id, full_name, business_name, county, cph_verified, membership_tier )`
    )
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as BuyerRequest) ?? null;
}

export interface NewBuyerRequestInput {
  category: string;
  title: string;
  description?: string | null;
  species?: string | null;
  breed?: string | null;
  quantity?: number | null;
  county?: string | null;
  budget_min?: number | null;
  budget_max?: number | null;
  details?: Record<string, string>;
  contact_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
}

/**
 * Post a wanted ad. The status the browser asks for is irrelevant — the
 * buyer_requests_enforce_review trigger decides, from the same admin setting
 * that governs listings.
 */
export async function createBuyerRequest(
  supabase: SupabaseClient,
  input: NewBuyerRequestInput
): Promise<{ id: string }> {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();
  if (userError) throw userError;
  if (!user) throw new Error('You need to be signed in to post a buyer request.');

  const { data, error } = await supabase
    .from('buyer_requests')
    .insert({ ...input, buyer_id: user.id, status: NEW_REQUEST_STATUS })
    .select('id')
    .single();

  if (error) throw error;
  return data as { id: string };
}

/** A seller answering a wanted ad. */
export async function respondToBuyerRequest(
  supabase: SupabaseClient,
  requestId: string,
  message: string
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('You need to be signed in to respond.');

  const { error } = await supabase
    .from('buyer_request_responses')
    .insert({ request_id: requestId, seller_id: user.id, message });
  if (error) throw error;
}

export async function fetchResponses(
  supabase: SupabaseClient,
  requestId: string
): Promise<BuyerRequestResponse[]> {
  const { data, error } = await supabase
    .from('buyer_request_responses')
    .select(
      '*, seller:profiles!buyer_request_responses_seller_id_fkey ( id, full_name, business_name, county )'
    )
    .eq('request_id', requestId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as BuyerRequestResponse[];
}

/** How many sellers have answered — safe to show publicly. */
export async function fetchResponseCount(
  supabase: SupabaseClient,
  requestId: string
): Promise<number> {
  const { data, error } = await supabase.rpc('buyer_request_response_count', {
    request: requestId,
  });
  if (error) return 0;
  return Number(data) || 0;
}

const CATEGORY_LABELS: Record<string, string> = {
  cattle: 'Cattle',
  sheep: 'Sheep',
  pigs: 'Pigs',
  horses: 'Horses & Ponies',
  goats: 'Goats',
  poultry: 'Poultry',
  deer: 'Deer',
  alpacas: 'Alpacas & Llamas',
  machinery: 'Machinery',
  deadstock: 'Deadstock / By-products',
  other: 'Other',
};

export function categoryLabel(category: string): string {
  return CATEGORY_LABELS[category] ?? category;
}

export function budgetLabel(request: BuyerRequest): string {
  const { budget_min: min, budget_max: max } = request;
  const fmt = (n: number) =>
    `£${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`;
  if (max != null) return `Up to ${fmt(max)}`;
  if (min != null) return `${fmt(min)}+`;
  return 'Open / negotiable';
}

/** "3 days ago" — same wording as the listings board. */
export function postedAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!then) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

/** Keys in `details` that are internal or already shown elsewhere. */
const HIDDEN_DETAIL_KEYS = new Set([
  'contactName',
  'contactEmail',
  'contactPhone',
  'termsAgreed',
  'contactConsent',
]);

export function visibleDetails(request: BuyerRequest): [string, string][] {
  const d = request.details ?? {};
  return Object.entries(d)
    .filter(([k, v]) => !HIDDEN_DETAIL_KEYS.has(k) && v != null && String(v).trim() !== '')
    .map(([k, v]) => [labelFor(k), String(v)]);
}

function labelFor(key: string): string {
  const map: Record<string, string> = {
    ageRange: 'Age range',
    weightRange: 'Weight range',
    sex: 'Sex',
    radius: 'Willing to travel',
    urgency: 'Timescale',
    assurance: 'Assurance scheme',
  };
  return map[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}
